import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Download } from "lucide-react";
import { formatDOP } from "@/lib/format";

const REASON_LABELS = {
  cliente_arrepentido: "Cliente se arrepintió",
  sin_stock: "Producto sin stock",
  error_vendedor: "Error del vendedor",
  otro: "Otro",
};

export default function OrdersAuditReport() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom + "T00:00:00Z");
      if (dateTo) params.set("date_to", dateTo + "T23:59:59Z");
      const { data } = await api.get(`/sales-orders/audit/report?${params.toString()}`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const downloadCsv = () => {
    if (!report) return;
    const header = "Orden;Fecha despacho;Estado;Cliente;Vendedor;Cajero;Total;Factura;Motivo cancelacion";
    const rows = report.orders.map((o) => [
      o.number,
      (o.dispatched_at || "").slice(0, 19).replace("T", " "),
      o.status,
      `"${o.customer_name || ""}"`,
      `"${o.vendor_name || ""}"`,
      `"${o.cashier_name || ""}"`,
      (o.total || 0).toFixed(2),
      o.invoice_number || "",
      `"${REASON_LABELS[o.cancellation_reason_code] || ""}"`,
    ].join(";"));
    const blob = new Blob([`\uFEFF${header}\n${rows.join("\n")}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria_ordenes_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Auditoría · Órdenes vs Facturas"
        subtitle="Reporte de control para detectar órdenes no cobradas o canceladas"
        testid="orders-audit-header"
      />

      <div className="bg-card border rounded-md p-3 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="overline">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="audit-date-from" />
        </div>
        <div>
          <Label className="overline">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="audit-date-to" />
        </div>
        <Button onClick={load} disabled={loading} data-testid="audit-run">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Generar
        </Button>
        <Button variant="outline" onClick={downloadCsv} disabled={!report} data-testid="audit-csv">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="audit-summary">
            <div className="bg-card border p-3 rounded"><div className="overline">Total órdenes</div><div className="text-2xl font-black">{report.summary.total}</div></div>
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border p-3 rounded">
              <div className="overline">Facturadas</div>
              <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300">{report.summary.facturadas}</div>
              <div className="text-xs mt-1">{formatDOP(report.summary.monto_facturado)}</div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border p-3 rounded">
              <div className="overline">Pendientes</div>
              <div className="text-2xl font-black text-amber-700 dark:text-amber-300">{report.summary.pendientes}</div>
              <div className="text-xs mt-1">{formatDOP(report.summary.monto_pendiente)}</div>
            </div>
            <div className="bg-red-50 dark:bg-red-950/30 border p-3 rounded">
              <div className="overline">Canceladas</div>
              <div className="text-2xl font-black text-red-700 dark:text-red-300">{report.summary.canceladas}</div>
              <div className="text-xs mt-1">{formatDOP(report.summary.monto_cancelado)}</div>
            </div>
          </div>

          <div className="bg-card border rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-muted/50 border-b sticky top-0">
                <tr>
                  <th className="p-2 text-left overline">Orden</th>
                  <th className="p-2 text-left overline">Fecha</th>
                  <th className="p-2 text-left overline">Estado</th>
                  <th className="p-2 text-left overline">Cliente</th>
                  <th className="p-2 text-left overline">Vendedor</th>
                  <th className="p-2 text-left overline">Cajero</th>
                  <th className="p-2 text-right overline">Total</th>
                  <th className="p-2 text-left overline">Factura</th>
                  <th className="p-2 text-left overline">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {report.orders.length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">Sin órdenes en el rango seleccionado</td></tr>
                )}
                {report.orders.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-accent/30" data-testid={`audit-row-${o.number}`}>
                    <td className="p-2 font-bold">{o.number}</td>
                    <td className="p-2 text-xs">{(o.dispatched_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2 text-xs uppercase font-semibold">{o.status.replace("_", " ")}</td>
                    <td className="p-2 text-xs">{o.customer_name}</td>
                    <td className="p-2 text-xs">{o.vendor_name || "—"}</td>
                    <td className="p-2 text-xs">{o.cashier_name || "—"}</td>
                    <td className="p-2 text-right data-cell">{formatDOP(o.total || 0)}</td>
                    <td className="p-2 text-xs">{o.invoice_number || "—"}</td>
                    <td className="p-2 text-xs">{REASON_LABELS[o.cancellation_reason_code] || "—"}
                      {o.cancellation_reason_text && <div className="text-[10px] text-muted-foreground">{o.cancellation_reason_text}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageContainer>
  );
}
